import { apiClient } from '@borradh-workspace/api-client';
import type { PendingRegenerateEdit } from '@borradh-workspace/contracts';
import { useQuery } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { type ReactNode, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { useDiscardVideoEdits } from '@/features/content-batches/api';
import { useRegenerateGraphic } from '@/features/graphics/api/regenerate-graphic';
import { queryKeys } from '@/lib/query-keys';
import { ComposerDecision } from '../composer-gate-context';
import { ImagePickerDialog } from './image-picker-dialog';

function describeEdit(edit: PendingRegenerateEdit): string {
  const where =
    edit.slideIndex === null ? 'this' : `slide ${edit.slideIndex + 1}`;
  if (edit.op === 'remove') return `remove ${where}`;
  return edit.note
    ? `redo ${where} — ${edit.note.replace(/[.!?]+$/, '')}`
    : `redo ${where}`;
}

/**
 * A re-roll Claire has PROPOSED — the SAME confirmation-card shape as a
 * fresh create (`GraphicDraftPreviewCard` / `VideoDraftPreviewCard`): a
 * sentence describing the change, "Edit" opening the real image picker so
 * the owner can swap the image before spending the render, and
 * Reject/Accept in the footer. Not a separate "staged edit" feature —
 * patching an item is the identical decision as creating one, so it gets
 * the identical card.
 *
 * A re-roll costs a render and replaces what is on screen, so the review
 * turn stages it rather than firing it — the proposal lands on the slot and
 * the owner presses Accept. Same rule the clip list editor uses for video,
 * where Accept is what spends the render.
 *
 * Read from the SERVER, not from the turn that proposed it. The proposal is
 * persisted on the item, so this survives a reload and a remount; a card
 * that held it in React would offer to spend a render the owner had already
 * declined, or lose one they had not answered yet.
 */
export function PendingReRoll({
  itemId,
  attemptId,
  graphicId,
  serviceId,
  onReRolled,
  onAnswered,
  fallback = null,
  resolvedWhenNotPending = false,
}: {
  /**
   * Tells the conversation what the owner decided. Accept and Reject act on
   * the server directly, so without a message Claire's history still said the
   * change was staged — and she told the owner to "approve it on the card"
   * over a card that had already been answered.
   */
  onAnswered?: (text: string) => void;
  itemId: string;
  /**
   * Which content_createContent/content_patchContent CALL this card came
   * from. Scopes both the react-query cache key AND the server request to
   * THIS attempt — without it every `PendingReRoll` mounted for the same
   * item (one per past redo in the transcript) reads the identical "live
   * cut" snapshot and reacts to the same server update together, which is
   * what let a fresh redo proposal light up every earlier card for the item.
   */
  attemptId?: string;
  graphicId: string;
  /** For the image picker's service-match ordering. Optional — the envelope
   * that carries a bare `graphic_status` card has no service context. */
  serviceId?: string | null;
  /**
   * The re-roll renders into a NEW graphic row. Without this the card keeps
   * polling the id it was born with — the request succeeds, a fresh graphic
   * renders, and the owner watches the old one sit there unchanged.
   */
  onReRolled: (graphicId: string) => void;
  /**
   * Rendered when there is nothing pending, or this card's own attempt is no
   * longer the live one. `GraphicStatusCard`'s staged branch passes its
   * dashed "not generated yet" notice here so that text still shows during
   * the query's loading window and once superseded; the finished-graphic
   * branch leaves it `null`, matching its previous "render nothing" default.
   */
  fallback?: ReactNode;
  /**
   * Only the STAGED card knows a change was proposed, so only it may read
   * "nothing pending" as "the proposal was answered". Everywhere else —
   * a fresh graphic still rendering, or a finished one — nothing pending is
   * the normal state, and the "Change not applied" notice replaced the
   * "Creating graphic…" spinner the moment the owner pressed Accept.
   */
  resolvedWhenNotPending?: boolean;
}) {
  const { data: state } = useQuery({
    queryKey: queryKeys.contentBatches.itemState(itemId, attemptId),
    queryFn: () =>
      apiClient.get<{
        pendingRegenerate: PendingRegenerateEdit[] | null;
        assetId: string | null;
        /** True once THIS attempt is no longer the item's live cut. Only
         *  meaningful when `attemptId` was sent — the endpoint reports the
         *  live cut's own state (never "superseded") when it's omitted. */
        superseded: boolean;
      }>(
        `content-batches/items/${itemId}/state${
          attemptId ? `?attemptId=${encodeURIComponent(attemptId)}` : ''
        }`
      ),
    enabled: Boolean(itemId),
    staleTime: 5_000,
    // A staged edit is written by a DIFFERENT turn than the one that
    // rendered this card — `content_patchContent` runs against the item,
    // not this component — so nothing here tells React Query the server
    // changed. Without a poll, "Ready to redo" only appeared after
    // something else happened to remount the card (a reload, a window
    // refocus), which read as the staged instruction being silently
    // dropped. Stop once a proposal is showing: from there the owner is
    // looking at it, and Accept/Reject drive their own state updates.
    refetchInterval: (query) =>
      query.state.data?.pendingRegenerate?.length ? false : 5_000,
  });

  const { regenerateGraphic, isRegenerating } = useRegenerateGraphic({
    onSuccess: (graphic) => {
      onReRolled(graphic.id);
      onAnswered?.(
        'I approved that change on the card — it is regenerating now. Do not regenerate it again.'
      );
    },
    onError: () => {
      firedRef.current = false;
    },
  });
  // SPENT ONCE. The button fired twice 165ms apart in testing — faster than the
  // pending flag propagates — and each press starts a render. A ref settles it
  // before React re-renders, which `isRegenerating` cannot.
  const firedRef = useRef(false);
  const { discardVideoEdits, isDiscarding } = useDiscardVideoEdits({
    onSuccess: () =>
      onAnswered?.(
        'I rejected that change on the card — it was discarded and nothing was generated.'
      ),
  });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [overrideAssetIds, setOverrideAssetIds] = useState<string[]>([]);

  const pending = state?.pendingRegenerate ?? null;

  // ONLY THE LIVE CARD OFFERS THE BUTTON.
  //
  // A card in the transcript is a historical object reading live state, so
  // every graphic card for this item saw the same pending proposal and every
  // one of them lit up — a column of identical approvals, all but the last of
  // them stale.
  //
  // With an `attemptId`, the query above is scoped to THIS card's own
  // attempt, so `state.assetId` is no longer "the live cut" — it is this
  // attempt's own asset, which trivially equals `graphicId` and would defeat
  // a graphic-id comparison here (every card would look live). `superseded`
  // is what the endpoint computes for exactly this: true once THIS attempt
  // is no longer the one the item points at.
  //
  // A card with no `attemptId` (persisted before the stamp existed, or the
  // initial create-content card) falls back to the old graphic-id check
  // against the live cut's asset — the pre-stamp behaviour, unchanged.
  const isStale = attemptId
    ? Boolean(state?.superseded)
    : Boolean(state?.assetId && state.assetId !== graphicId);

  // Only a single WHOLE-ASSET refine gets an image picker. `sourceAssetIds`
  // only reaches the renderer on the ordinary full-generation path — the
  // same one `useGenerateGraphic` already uses at create time. A named-slide
  // refine goes through the carousel slide-edit path instead
  // (`refineCarouselSlidesGpt`), which edits the slide's OWN prior pixels
  // from a text note and has no reference-image input at all: an id sent
  // here would silently do nothing. A `remove` has no image to swap, and
  // several simultaneous edits can't be collapsed onto one picker without
  // inventing which edit it's for.
  const edits = pending ?? [];
  const singleRefine =
    edits.length === 1 && edits[0].op === 'refine' ? edits[0] : null;
  const isWholeAsset = singleRefine?.slideIndex === null;
  const canSwapImage = isWholeAsset;

  const sentence =
    edits.length === 1
      ? `I'll ${describeEdit(edits[0])}.`
      : `I'll make ${edits.length} changes: ${edits.map(describeEdit).join('; ')}.`;

  const busy = isRegenerating || isDiscarding;

  const fire = () => {
    if (firedRef.current) return;
    firedRef.current = true;
    // One entry for the WHOLE asset is `scope: 'all'`; named slides are
    // `slideEdits`, which re-renders those and carries the rest across
    // verbatim. The two shapes say the same thing for one slide.
    const wholeAsset = edits.find((e) => e.slideIndex === null);
    regenerateGraphic({
      graphicId,
      ...(overrideAssetIds.length ? { sourceAssetIds: overrideAssetIds } : {}),
      ...(wholeAsset
        ? {
            scope: 'all' as const,
            ...(wholeAsset.note
              ? { refinementInstruction: wholeAsset.note }
              : {}),
            ...(wholeAsset.intent
              ? { regenerationIntent: wholeAsset.intent }
              : {}),
          }
        : {
            slideEdits: edits.map((edit) => ({
              slideIndex: edit.slideIndex as number,
              op: edit.op,
              ...(edit.note ? { note: edit.note } : {}),
            })),
          }),
    });
  };

  const gateId = `pending-reroll:${itemId}:${attemptId ?? 'live'}`;

  if (!state || isStale) return <>{fallback}</>;
  // Loaded, current, and nothing pending: the proposal was answered. The
  // fallback says "Change staged — open to approve or reject", which after a
  // Reject described a change that no longer existed.
  if (!pending?.length) {
    if (!resolvedWhenNotPending) return <>{fallback}</>;
    return (
      <p
        data-testid="claire-card-graphic-status-resolved"
        className="rounded-lg border border-dashed bg-card p-3 text-xs text-muted-foreground"
      >
        Change not applied — the graphic is unchanged.
      </p>
    );
  }

  return (
    <ComposerDecision
      id={gateId}
      gate={{
        sentence,
        onAccept: fire,
        onReject: () => discardVideoEdits(itemId),
      }}
    >
      <div className="mt-2 w-full rounded-lg border bg-card p-3 sm:max-w-sm">
        <div className="flex items-start gap-2 text-xs">
          <Sparkles className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
          <p>{sentence}</p>
        </div>
        {/* The cost, stated where the money is spent. */}
        <p className="mt-1.5 pl-5.5 text-[11px] text-muted-foreground">
          Creates a new version. You can still use the current one.
        </p>

        <div
          className={`mt-3 flex items-center ${canSwapImage ? 'justify-between' : 'justify-end'}`}
        >
          {canSwapImage && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              disabled={busy}
              onClick={() => setPickerOpen(true)}
            >
              Edit
            </Button>
          )}
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              disabled={busy}
              // Discard CLEARS the proposal server-side. It used to clear only
              // React state, so a declined re-roll came back on the next load
              // still offering to spend the render.
              onClick={() => discardVideoEdits(itemId)}
            >
              Reject
            </Button>
            <Button
              type="button"
              size="sm"
              className="h-7 text-xs"
              disabled={busy}
              onClick={fire}
            >
              {isRegenerating ? 'Starting…' : 'Accept'}
            </Button>
          </div>
        </div>

        {canSwapImage && (
          <ImagePickerDialog
            open={pickerOpen}
            onOpenChange={setPickerOpen}
            selectedIds={overrideAssetIds}
            onConfirm={setOverrideAssetIds}
            serviceId={serviceId ?? undefined}
            minCount={0}
            maxCount={10}
            title="Choose images"
            confirmLabel="Use selected images"
          />
        )}
      </div>
    </ComposerDecision>
  );
}
